import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { User, Play, LogOut } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { API } from '@/config';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useContext(AuthContext);
  const [metrics, setMetrics] = useState({
    total_users: 0,
    total_videos: 0,
    revenue_paypal_usd_total: 0,
    revenue_plin_pen_total: 0,
    payments_count_total: 0,
    paypal_ledger_since: '',
    daily: [],
  });

  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user]);

  useEffect(() => {
    if (!user || user.role !== 'admin' || !token) return;
    const fetchMetrics = () => {
      axios.get(`${API}/admin/metrics`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => setMetrics(res.data))
        .catch(() => {}); // silent - next poll retries, no need to spam toasts on a background fetch
    };
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 60000);
    return () => clearInterval(interval);
  }, [user, token]);

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div data-testid="dashboard-header" className="mb-12">
            <h1 className="text-5xl font-bold text-amber-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Mi Dashboard
            </h1>
            <p className="text-xl text-gray-700">
              Bienvenido, {user.name}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Profile Card */}
            <div data-testid="profile-card" className="glass rounded-2xl p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center mr-4">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-amber-900">Perfil</h3>
                  <p className="text-sm text-gray-600">Tu información</p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Email:</span> {user.email}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Rol:</span> {user.role === 'admin' ? 'Administrador' : 'Usuario'}
                </p>
              </div>
            </div>
          </div>

          {/* Admin metrics */}
          {user.role === 'admin' && (
            <div className="mb-12">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardHeader><CardTitle className="text-sm text-muted-foreground">Usuarios totales</CardTitle></CardHeader>
                  <CardContent><p className="text-3xl font-bold">{metrics.total_users}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-sm text-muted-foreground">Ingresos PayPal (USD)</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold">${(metrics.revenue_paypal_usd_total || 0).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground mt-1">desde {metrics.paypal_ledger_since}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-sm text-muted-foreground">Ingresos Plin (S/)</CardTitle></CardHeader>
                  <CardContent><p className="text-3xl font-bold">S/ {(metrics.revenue_plin_pen_total || 0).toFixed(2)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-sm text-muted-foreground">Compras/suscripciones</CardTitle></CardHeader>
                  <CardContent><p className="text-3xl font-bold">{metrics.payments_count_total || 0}</p></CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader><CardTitle className="text-sm text-muted-foreground">Tendencia — últimos 30 días</CardTitle></CardHeader>
                <CardContent>
                  {metrics.payments_count_total === 0 ? (
                    <p className="text-sm text-muted-foreground py-12 text-center">Aún no hay pagos registrados.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={metrics.daily}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="plin" orientation="right" tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        <Line yAxisId="left" type="monotone" dataKey="revenue_paypal_usd" name="PayPal (USD)" stroke="#2563eb" dot={false} />
                        <Line yAxisId="plin" type="monotone" dataKey="revenue_plin_pen" name="Plin (S/)" stroke="#16a34a" dot={false} />
                        <Line yAxisId="right" type="monotone" dataKey="payments_count" name="Pagos/día" stroke="#f59e0b" dot={false} strokeDasharray="4 2" />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Quick Actions */}
          <div data-testid="quick-actions" className="glass rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-amber-900 mb-6">Acciones Rápidas</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Button
                onClick={() => navigate('/gallery')}
                className="btn-peru py-6 flex flex-col items-center"
              >
                <Play className="w-6 h-6 mb-2" />
                Ver Galería
              </Button>
              <Button
                onClick={logout}
                variant="outline"
                className="border-2 border-red-300 text-red-600 hover:bg-red-50 py-6 flex flex-col items-center"
              >
                <LogOut className="w-6 h-6 mb-2" />
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;